const RequirementCheckStatus = Object.freeze({
    PASSED: 'passed',
    FAILED: 'failed',
    SKIPPED: 'skipped',
})

const createCheck = (id, status, reason = null) => ({
    id,
    status,
    blocking: true,
    reason,
})

const passed = (id, messageKey = null, fallbackMessage = null) => createCheck(
    id,
    RequirementCheckStatus.PASSED,
    messageKey && fallbackMessage ? { messageKey, fallbackMessage } : null,
)
const skipped = id => createCheck(id, RequirementCheckStatus.SKIPPED)
const failed = (id, messageKey, fallbackMessage) => createCheck(
    id,
    RequirementCheckStatus.FAILED,
    { messageKey, fallbackMessage },
)

const simpleRequirementChecks = ({
    questions: suppliedQuestions,
    hasQuestion: legacyHasQuestion,
    questionState: legacyQuestionState,
    placements,
    missingProperties,
}) => {
    const questions = Array.isArray(suppliedQuestions)
        ? suppliedQuestions
        : (legacyHasQuestion ? [{ state: legacyQuestionState }] : [])
    const checks = []
    const hasQuestion = questions.length > 0
    const unansweredQuestions = questions.filter(question => (
        question.state !== true && question.state !== false
    ))
    const hasPositiveAnswer = questions.some(question => question.state === true)
    const allQuestionsNegative = hasQuestion && questions.every(question => question.state === false)

    if (!hasQuestion) {
        checks.push(skipped('list-question'))
    } else if (unansweredQuestions.length > 0) {
        checks.push(failed(
            'list-question',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-answer-list-question',
            unansweredQuestions.length === 1
                ? 'Answer the question for this substance list.'
                : 'Answer all questions for these substance lists.',
        ))
    } else if (allQuestionsNegative) {
        checks.push(passed(
            'list-question',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-no-relevant-substances',
            'Answered No — no relevant substances declared.',
        ))
    } else {
        checks.push(passed('list-question'))
    }

    const shouldCheckSubstances = !hasQuestion || hasPositiveAnswer
    if (!shouldCheckSubstances) {
        checks.push(skipped('selected-substances'))
        checks.push(skipped('required-properties'))
        return checks
    }

    if (placements.length === 0) {
        checks.push(failed(
            'selected-substances',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-select-substance',
            'Select at least one substance or answer No.',
        ))
        checks.push(skipped('required-properties'))
        return checks
    }

    checks.push(passed('selected-substances'))
    checks.push(missingProperties.length === 0
        ? passed(
            'required-properties',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-substance-information-complete',
            'Required substance information is complete.',
        )
        : failed(
            'required-properties',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-complete-substance-information',
            'Complete the required information for the selected substances.',
        ))

    return checks
}

const complexRequirementChecks = complete => [
    complete
        ? passed(
            'complex-declaration',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-component-declaration-complete',
            'The substance declaration is complete for every component.',
        )
        : failed(
            'complex-declaration',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-complete-component-declaration',
            'Complete the substance declaration for every component.',
        ),
]

const scipNumberCheck = ({ applicable, required, value }) => {
    if (!applicable || !required) {
        return skipped('scip-number')
    }

    return String(value || '').trim().length > 0
        ? passed(
            'scip-number',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-scip-number-provided',
            'SCIP number provided.',
        )
        : failed(
            'scip-number',
            'Page(ReceivedRequest):Component(ComplianceInformation):reason-enter-scip-number',
            'Enter a SCIP number.',
        )
}

const requirementStatus = checks => (
    checks.some(check => check.blocking && check.status === RequirementCheckStatus.FAILED)
        ? 'needs-input'
        : 'complete'
)

const blockingReasons = checks => (checks || [])
    .filter(check => check.blocking && check.status === RequirementCheckStatus.FAILED && check.reason)
    .map(check => ({ id: check.id, ...check.reason }))

const completedReasons = checks => (checks || [])
    .filter(check => check.status === RequirementCheckStatus.PASSED && check.reason)
    .map(check => ({ id: check.id, ...check.reason }))

export {
    RequirementCheckStatus,
    blockingReasons,
    completedReasons,
    complexRequirementChecks,
    requirementStatus,
    scipNumberCheck,
    simpleRequirementChecks,
}
